// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene
export const SCENE_COLORS = {
  scene1: { primaryColor: '#6B7280', accentColor: '#EF4444' },
  scene2: { primaryColor: '#22C55E', accentColor: '#ffffff' },
  scene3: { primaryColor: '#ffffff', accentColor: '#F59E0B' },
  scene4: { primaryColor: '#F59E0B', accentColor: '#ffffff' },
  scene5: { primaryColor: '#EF4444', accentColor: '#ffffff' },
  scene6: { primaryColor: '#3B82F6', accentColor: '#FCD34D' },
  scene7: { primaryColor: '#FCD34D', accentColor: '#ffffff' },
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/gdm-quiet-phone/scene1-quiet-phone.png',
  scene2: 'images/gdm-quiet-phone/scene2-ringing-phone.png',
  scene3: 'images/gdm-quiet-phone/scene3-neighborhood.png',
  scene4: 'images/gdm-quiet-phone/scene4-technician.png',
  scene5: 'images/gdm-quiet-phone/scene5-search-results.png',
  scene6: 'images/gdm-quiet-phone/scene6-analytics.png',
  scene7: 'images/gdm-quiet-phone/scene7-report.png',
};

// Word timings extracted from audio using Whisper (incredibly-fast-whisper)
export const WORD_TIMINGS: WordTiming[] = [
  { text: 'Your', startMs: 0, endMs: 180 },
  { text: 'phone.', startMs: 180, endMs: 660 },
  { text: 'Quiet.', startMs: 660, endMs: 1560 },
  { text: 'Your', startMs: 1560, endMs: 2140 },
  { text: "competitor's", startMs: 2140, endMs: 2680 },
  { text: 'phone.', startMs: 2680, endMs: 3120 },
  { text: 'Ringing.', startMs: 3120, endMs: 3960 },
  { text: 'Same', startMs: 3960, endMs: 4440 },
  { text: 'city.', startMs: 4440, endMs: 4980 },
  { text: 'Same', startMs: 4980, endMs: 5440 },
  { text: 'services.', startMs: 5440, endMs: 6160 },
  { text: 'Different', startMs: 6160, endMs: 6760 },
  { text: 'results.', startMs: 6760, endMs: 7480 },
  { text: "They're", startMs: 7480, endMs: 7940 },
  { text: 'getting', startMs: 7940, endMs: 8160 },
  { text: 'the', startMs: 8160, endMs: 8320 },
  { text: 'emergency', startMs: 8320, endMs: 8660 },
  { text: 'repairs.', startMs: 8660, endMs: 9420 },
  { text: 'The', startMs: 9420, endMs: 9820 },
  { text: 'commercial', startMs: 9820, endMs: 10140 },
  { text: 'installs.', startMs: 10140, endMs: 10880 },
  { text: 'The', startMs: 10880, endMs: 11380 },
  { text: 'same', startMs: 11380, endMs: 11560 },
  { text: 'day', startMs: 11560, endMs: 11720 },
  { text: 'jobs.', startMs: 11720, endMs: 12400 },
  { text: 'Why?', startMs: 12400, endMs: 13440 },
  { text: 'They', startMs: 13440, endMs: 14060 },
  { text: 'own', startMs: 14060, endMs: 14220 },
  { text: 'the', startMs: 14220, endMs: 14320 },
  { text: 'search', startMs: 14320, endMs: 14520 },
  { text: 'terms', startMs: 14520, endMs: 14820 },
  { text: 'you', startMs: 14820, endMs: 15000 },
  { text: "don't.", startMs: 15000, endMs: 15400 },
  { text: 'We', startMs: 15400, endMs: 15880 },
  { text: 'map', startMs: 15880, endMs: 16060 },
  { text: 'your', startMs: 16060, endMs: 16240 },
  { text: 'entire', startMs: 16240, endMs: 16440 },
  { text: 'market.', startMs: 16440, endMs: 17120 },
  { text: 'Who', startMs: 17120, endMs: 17460 },
  { text: 'ranks', startMs: 17460, endMs: 17680 },
  { text: 'above', startMs: 17680, endMs: 17880 },
  { text: 'you.', startMs: 17880, endMs: 18300 },
  { text: "What's", startMs: 18300, endMs: 18720 },
  { text: 'working.', startMs: 18720, endMs: 19200 },
  { text: "What's", startMs: 19200, endMs: 19560 },
  { text: 'not.', startMs: 19560, endMs: 19960 },
  { text: 'Free', startMs: 19960, endMs: 20600 },
  { text: 'Market', startMs: 20600, endMs: 20820 },
  { text: 'Report.', startMs: 20820, endMs: 21460 },
  { text: 'Grab', startMs: 21460, endMs: 21980 },
  { text: 'yours', startMs: 21980, endMs: 22140 },
  { text: 'now.', startMs: 22140, endMs: 22580 },
];

// Scene definitions with word groupings (updated to match Whisper-extracted timing)
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 1560,
    words: WORD_TIMINGS.slice(0, 3),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Contrast',
    startMs: 1560,
    endMs: 3960,
    words: WORD_TIMINGS.slice(3, 7),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Setup',
    startMs: 3960,
    endMs: 7480,
    words: WORD_TIMINGS.slice(7, 13),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 4,
    name: 'Pain',
    startMs: 7480,
    endMs: 12400,
    words: WORD_TIMINGS.slice(13, 25),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomInLower,
  },
  {
    id: 5,
    name: 'Question',
    startMs: 12400,
    endMs: 15400,
    words: WORD_TIMINGS.slice(25, 33),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 6,
    name: 'Solution',
    startMs: 15400,
    endMs: 19960,
    words: WORD_TIMINGS.slice(33, 46),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 7,
    name: 'CTA',
    startMs: 19960,
    endMs: 22580,
    words: WORD_TIMINGS.slice(46),
    ...SCENE_COLORS.scene7,
    backgroundImage: SCENE_IMAGES.scene7,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene (after voiceover) - 4 seconds for full logo playback
export const LOGO_SCENE = {
  startMs: 22580,
  endMs: 26580,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
